import { prefersReducedMotion, throttle } from '@theme/utilities';
import { getScrollEventTarget } from '@theme/scroll-container';

const HEADING_SELECTOR = '.intro_section .text-block > :is(h1, h2, h3, h4, h5, h6, p)';
const WORD_CLASS = 'intro-heading-word';

/**
 * Splits a heading's text into per-word spans so each word can be highlighted individually.
 * Assumes the heading contains plain text only (no nested inline formatting).
 * @param {HTMLElement} heading
 */
function splitIntoWords(heading) {
  const words = heading.textContent?.trim().split(/\s+/) ?? [];
  heading.textContent = '';

  words.forEach((word, index) => {
    const span = document.createElement('span');
    span.className = WORD_CLASS;
    span.textContent = word;
    heading.appendChild(span);

    if (index < words.length - 1) {
      heading.appendChild(document.createTextNode(' '));
    }
  });

  return Array.from(heading.querySelectorAll(`.${WORD_CLASS}`));
}

/**
 * Maps a heading's scroll position to a 0-1 progress value used to stagger word opacity.
 * @param {HTMLElement} heading
 * @param {HTMLElement[]} words
 */
function updateProgress(heading, words) {
  const rect = heading.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const progress = Math.min(Math.max((viewportHeight - rect.top) / (viewportHeight * 0.85), 0), 1);

  const total = words.length;
  const transitionWidth = 0.6 / total;

  words.forEach((word, index) => {
    const start = index / total;
    const local = Math.min(Math.max((progress - start) / transitionWidth, 0), 1);
    word.style.opacity = String(0.4 + 0.6 * local);
  });
}

function init() {
  const headings = /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll(HEADING_SELECTOR)));
  if (headings.length === 0) return;

  const headingWords = headings.map((heading) => [heading, splitIntoWords(heading)]);
  if (prefersReducedMotion()) return;

  const update = () => headingWords.forEach(([heading, words]) => updateProgress(heading, words));
  const onScroll = throttle(update, 16);

  // The theme scrolls `.page-wrapper` (not `window`) on desktop, so the scroll
  // listener must be attached to whichever element actually emits scroll events.
  getScrollEventTarget().addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  update();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
